import React, { useState, useEffect, useCallback } from 'react'
import { useMedications } from '../../hooks/useMedications'
import { formatActivityTime, formatActivityDate, getActivityStatusText } from '../../utils/activityFormat'

interface HistoryActivity {
  id: string
  medicationId: string
  medicationName: string
  scheduledTime: string
  actualTime: Date
  status: 'taken' | 'missed' | 'skipped' | 'stock_adjusted'
  notes?: string
  stockBefore?: number
  stockAfter?: number
  createdAt: Date
}

interface ActivityHistoryProps {
  selectedDate?: Date | null
  limit?: number
  showDateFilter?: boolean
  maxItems?: number
  compact?: boolean
  // External control props for persistence
  selectedMonth?: number
  selectedYear?: number
  filter?: 'all' | 'taken' | 'missed' | 'stock_adjusted'
  groupByDate?: boolean
  searchText?: string
  onMonthChange?: (month: number) => void
  onYearChange?: (year: number) => void
  onFilterChange?: (filter: 'all' | 'taken' | 'missed' | 'stock_adjusted') => void
  onGroupByDateChange?: (groupByDate: boolean) => void
  onSearchTextChange?: (searchText: string) => void
}

const ActivityHistory: React.FC<ActivityHistoryProps> = ({ 
  selectedDate, 
  limit = 20, 
  showDateFilter = true, 
  maxItems, 
  compact = false,
  // External control props
  selectedMonth: externalSelectedMonth,
  selectedYear: externalSelectedYear,
  filter: externalFilter,
  groupByDate: externalGroupByDate,
  searchText: externalSearchText,
  onMonthChange,
  onYearChange,
  onFilterChange,
  onGroupByDateChange,
  onSearchTextChange
}) => {
  const [activities, setActivities] = useState<HistoryActivity[]>([])
  const [loading, setLoading] = useState(true)
  
  // Use external state if provided, otherwise use internal state
  const [internalFilter, setInternalFilter] = useState<'all' | 'taken' | 'missed' | 'stock_adjusted'>('all')
  const [internalGroupByDate, setInternalGroupByDate] = useState(true)
  const [internalSearchText, setInternalSearchText] = useState('')
  
  const filter = externalFilter !== undefined ? externalFilter : internalFilter
  const groupByDate = externalGroupByDate !== undefined ? externalGroupByDate : internalGroupByDate
  const searchText = externalSearchText !== undefined ? externalSearchText : internalSearchText
  
  const handleFilterChange = (value: 'all' | 'taken' | 'missed' | 'stock_adjusted') => {
    if (onFilterChange) {
      onFilterChange(value)
    } else {
      setInternalFilter(value)
    }
  }
  
  const setGroupByDateState = (value: boolean) => {
    if (onGroupByDateChange) {
      onGroupByDateChange(value)
    } else {
      setInternalGroupByDate(value)
    }
  }
  
  const setSearchTextState = (value: string) => {
    if (onSearchTextChange) {
      onSearchTextChange(value)
    } else {
      setInternalSearchText(value)
    }
  }
  
  // Month/Year filter state - use external if provided
  const [internalSelectedMonth, setInternalSelectedMonth] = useState(() => {
    const today = new Date()
    return today.getMonth()
  })
  const [internalSelectedYear, setInternalSelectedYear] = useState(() => {
    const today = new Date()
    return today.getFullYear()
  })
  
  const selectedMonth = externalSelectedMonth !== undefined ? externalSelectedMonth : internalSelectedMonth
  const selectedYear = externalSelectedYear !== undefined ? externalSelectedYear : internalSelectedYear
  
  const setSelectedMonth = (value: number) => {
    if (onMonthChange) {
      onMonthChange(value)
    } else {
      setInternalSelectedMonth(value)
    }
  }
  
  const setSelectedYear = (value: number) => {
    if (onYearChange) {
      onYearChange(value)
    } else {
      setInternalSelectedYear(value)
    }
  }
  
  const { getDayActivities, getMonthlyHistory, debugActivities } = useMedications()

  const loadActivities = useCallback(async () => {
    setLoading(true)
    try {
      let activityData: any[] = []
      
      if (selectedDate) {
        // Load specific day
        console.log('🔍 Loading activities for date:', selectedDate)
        activityData = await getDayActivities(selectedDate)
      } else {
        // Load selected month/year
        console.log('🔍 Loading monthly history for:', selectedYear, selectedMonth)
        activityData = await getMonthlyHistory(selectedYear, selectedMonth)
      }
      
      console.log('📊 Raw activity data loaded:', activityData.length, activityData)
      
      // Convert and normalize data
      const normalizedActivities = activityData.map(activity => ({
        id: activity.id || `${activity.medicationId}-${activity.actualTime?.getTime()}`,
        medicationId: activity.medicationId || '',
        medicationName: activity.medicationName || 'Medicamento desconocido',
        scheduledTime: activity.scheduledTime || '',
        actualTime: activity.actualTime instanceof Date ? activity.actualTime : new Date(activity.actualTime || Date.now()),
        status: activity.status || 'taken',
        notes: activity.notes || '',
        stockBefore: activity.stockBefore,
        stockAfter: activity.stockAfter,
        createdAt: activity.createdAt instanceof Date ? activity.createdAt : new Date(activity.createdAt || Date.now())
      }))
      
      // Sort by most recent first
      const sortedActivities = normalizedActivities
        .sort((a, b) => new Date(b.actualTime).getTime() - new Date(a.actualTime).getTime())
        .slice(0, limit)
      
      console.log('✅ Processed activities:', sortedActivities.length, sortedActivities)
      setActivities(sortedActivities as HistoryActivity[])
    } catch (error) {
      console.error('❌ Error loading activities:', error)
    } finally {
      setLoading(false)
    }
  }, [selectedDate, limit, selectedMonth, selectedYear, getDayActivities, getMonthlyHistory])

  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  // Navigation functions
  const goToPreviousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }

  const goToCurrentMonth = () => {
    const today = new Date()
    setSelectedMonth(today.getMonth())
    setSelectedYear(today.getFullYear())
  }

  const isCurrentMonth = () => {
    const today = new Date()
    return selectedMonth === today.getMonth() && selectedYear === today.getFullYear()
  }

  const filteredActivities = activities.filter(activity => {
    // Filter by status
    const matchesStatus = filter === 'all' || activity.status === filter
    
    // Filter by search text (medication name)
    const matchesSearch = !searchText || 
      activity.medicationName.toLowerCase().includes(searchText.toLowerCase())
    
    return matchesStatus && matchesSearch
  })

  const groupedActivities = groupByDate ? groupActivitiesByDate(filteredActivities) : null

  const formatDate = (date: Date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoy'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer'
    } else {
      return date.toLocaleDateString('es-ES', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    }
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedDate 
              ? `Actividades - ${formatDate(selectedDate)}` 
              : `Historial de Actividades - ${new Date(selectedYear, selectedMonth).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`
            }
          </h3>
          {!selectedDate && !loading && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {activities.length === 0 
                ? 'No hay actividades registradas este mes'
                : `${activities.length} actividad${activities.length !== 1 ? 'es' : ''} encontrada${activities.length !== 1 ? 's' : ''}`
              }
              {filter !== 'all' && filteredActivities.length !== activities.length && (
                <span> • {filteredActivities.length} mostrada{filteredActivities.length !== 1 ? 's' : ''} después del filtro</span>
              )}
            </p>
          )}
        </div>
        
        {/* Filters */}
        {showDateFilter && (
          <div className="flex flex-wrap items-center gap-3">
            {/* Month/Year navigation - only show when not viewing specific date */}
            {!selectedDate && (
            <>
              {/* Quick navigation buttons */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={goToPreviousMonth}
                  className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Mes anterior"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={goToNextMonth}
                  className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Mes siguiente"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {!isCurrentMonth() && (
                  <button
                    onClick={goToCurrentMonth}
                    className="ml-2 px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary/80 transition-colors"
                    title="Ir al mes actual"
                  >
                    Hoy
                  </button>
                )}
                
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">Mes:</label>
                <select 
                  value={selectedMonth}
                  onChange={(e) => {
                    const month = parseInt(e.target.value)
                    setSelectedMonth(month)
                  }}
                  className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(2024, i).toLocaleDateString('es-ES', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">Año:</label>
                <select 
                  value={selectedYear}
                  onChange={(e) => {
                    const year = parseInt(e.target.value)
                    setSelectedYear(year)
                  }}
                  className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    )
                  })}
                </select>
              </div>

              <div className="border-l border-gray-300 dark:border-gray-600 h-6 mx-2"></div>
            </>
          )}

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 dark:text-gray-300">Filtro:</label>
            <select 
              value={filter}
              onChange={(e) => {
                const newFilter = e.target.value as 'all' | 'taken' | 'missed' | 'stock_adjusted'
                handleFilterChange(newFilter)
              }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">Todas</option>
              <option value="taken">Tomadas</option>
              <option value="missed">Perdidas</option>
              <option value="stock_adjusted">Stock ajustado</option>
            </select>
          </div>
          
          {!selectedDate && (
            <button
              onClick={() => setGroupByDateState(!groupByDate)}
              className="text-sm text-primary hover:text-primary/80 px-3 py-1 border border-primary rounded-md hover:bg-primary/10 transition-colors"
            >
              {groupByDate ? '📋 Vista lista' : '📅 Agrupar por fecha'}
            </button>
          )}
          
          {/* Search input */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 dark:text-gray-300">Buscar:</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchTextState(e.target.value)}
              placeholder="Nombre del medicamento..."
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-48"
            />
            {searchText && (
              <button
                onClick={() => setSearchTextState('')}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                title="Limpiar búsqueda"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          </div>
        )}
      </div>

      {filteredActivities.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="mb-2">No hay actividades que mostrar</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {selectedDate 
              ? 'No se registraron medicamentos en esta fecha'
              : 'Comienza a tomar tus medicamentos para ver tu historial aquí'
            }
          </p>
          {!selectedDate && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
              💡 <strong>Tip:</strong> Una vez que registres medicamentos tomados, aparecerán aquí para ayudarte a seguir tu progreso
            </div>
          )}
        </div>
      ) : groupByDate && groupedActivities ? (
        <div className="space-y-6">
          {Object.entries(groupedActivities).map(([dateStr, dayActivities]) => (
            <div key={dateStr} className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-2">
                {formatDate(new Date(dateStr))}
              </h4>
              <div className="space-y-3">
                {(maxItems ? dayActivities.slice(0, maxItems) : dayActivities).map((activity) => (
                  <ActivityCard key={activity.id} activity={activity} compact={compact} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {(maxItems ? filteredActivities.slice(0, maxItems) : filteredActivities).map((activity) => (
            <ActivityCard key={activity.id} activity={activity} compact={compact} />
          ))}
        </div>
      )}
    </div>
  )
}

const ActivityCard: React.FC<{ activity: HistoryActivity; compact?: boolean }> = ({ activity, compact = false }) => {
  const getActivityIcon = (status: string) => {
    switch (status) {
      case 'taken':
        return (
          <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )
      case 'missed':
        return (
          <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )
      case 'stock_adjusted':
        return (
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 3v10a2 2 0 002 2h6a2 2 0 002-2V7H7zM5 7h14l-1 12H6L5 7z" />
            </svg>
          </div>
        )
      default:
        return (
          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        )
    }
  }

  // formatTime and formatActivityDate are now imported from utils
  // getStatusText is now imported as getActivityStatusText from utils

  if (compact) {
    return (
      <div className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="w-6 h-6 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {activity.medicationName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatActivityTime(activity.actualTime)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      {getActivityIcon(activity.status)}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {activity.medicationName}
          </h4>
          <div className="text-right ml-2 flex-shrink-0">
            <div className="text-xs font-medium text-gray-900 dark:text-white">
              {formatActivityTime(activity.actualTime)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatActivityDate(activity.actualTime)}
            </div>
          </div>
        </div>
        
        <div className="mt-1 flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-300">
          <span className="capitalize">{getActivityStatusText(activity.status)}</span>
          {activity.scheduledTime && (
            <span>Programado: {activity.scheduledTime}</span>
          )}
          {activity.status === 'stock_adjusted' && activity.stockBefore !== undefined && activity.stockAfter !== undefined && (
            <span>Stock: {activity.stockBefore} → {activity.stockAfter}</span>
          )}
        </div>
        
        {activity.notes && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {activity.notes}
          </p>
        )}
      </div>
    </div>
  )
}

// Helper function to group activities by date
function groupActivitiesByDate(activities: HistoryActivity[]): Record<string, HistoryActivity[]> {
  return activities.reduce((groups, activity) => {
    const dateKey = activity.actualTime.toDateString()
    if (!groups[dateKey]) {
      groups[dateKey] = []
    }
    groups[dateKey].push(activity)
    return groups
  }, {} as Record<string, HistoryActivity[]>)
}

export default ActivityHistory 